import React from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';

import svgMap from '../svgs/Common/svgMap';

const Container = styled.div`
  position: absolute;
  top: ${({ top }) => top || 0};
  left: ${({ left }) => left || 0};
`;

const fillAnimationY = keyframes`
  from {
    clip-path: inset(100% 0% 0% 0%);
  }
`;

const fillAnimationX = keyframes`
  from {
    clip-path: inset(0% 100% 0% 0%);
  }
`;

const getClipPath = ({ percent, isFilledHorizontally }) => {
  if (isFilledHorizontally) {
    return `0% ${100 - percent}% 0% 0%`;
  }
  return `${100 - percent}% 0% 0% 0%`;
};

const getFillAnimation = ({ isFilledHorizontally }) => {
  if (isFilledHorizontally) {
    return fillAnimationX;
  }
  return fillAnimationY;
};

const AnimatedIcon = styled.img`
  animation: ${getFillAnimation} 1s linear;
  clip-path: inset(${getClipPath});
  width: ${({ width }) => width || 'auto'};
  height: ${({ height }) => height || 'auto'};
  position: absolute;
  overflow: hidden;
  object-fit: contain;
`;

const ProgressIcon = styled.img`
  position: absolute;
  width: ${({ width }) => width || 'auto'};
  height: ${({ height }) => height || 'auto'};
  overflow: hidden;
  object-fit: contain;
`;

const PROGRESS_TYPE = {
  BOOK: 'Book',
  RADIO: 'Radio',
  PORTAL: 'Portal',
  BATTERY: 'Battery',
};

const AnimatedFill = ({
  progressType,
  isFilledHorizontally,
  top,
  left,
  width,
  height,
  completeRatio,
  iconAltText,
}) => {
  const iconSVG = svgMap[`${progressType}Icon`];
  const filledSvg = svgMap[`${progressType}IconFilled`];
  return (
    <Container top={top} left={left}>
      <AnimatedIcon
        width={width}
        height={height}
        src={filledSvg}
        percent={completeRatio * 100}
        isFilledHorizontally={isFilledHorizontally}
        alt=""
      />
      <ProgressIcon width={width} height={height} src={iconSVG} alt={iconAltText} />
    </Container>
  );
};

AnimatedFill.propTypes = {
  top: PropTypes.number,
  left: PropTypes.number,
  width: PropTypes.any,
  height: PropTypes.any,
  completeRatio: PropTypes.number,
  progressType: PropTypes.string,
  isFilledHorizontally: PropTypes.bool,
  iconAltText: PropTypes.string,
};

AnimatedFill.defaultProps = {
  width: 'auto',
  height: 'auto',
  top: 0,
  left: 0,
  completeRatio: 0,
  progressType: PROGRESS_TYPE.BATTERY,
  isFilledHorizontally: false,
  iconAltText: '',
};

export const BatteryIcon = (props) => (
  <AnimatedFill
    progressType={PROGRESS_TYPE.BATTERY}
    {...props}
    isFilledHorizontally
    iconAltText="Glyph-powered Battery"
  />
);

// this icon's ratio need to be reduced to 52% to match the filling area of the image.
export const RadioIcon = ({ completeRatio, ...props }) => (
  <AnimatedFill
    progressType={PROGRESS_TYPE.RADIO}
    completeRatio={completeRatio * 0.52}
    {...props}
    iconAltText="Radio Transmitter"
  />
);

RadioIcon.propTypes = {
  completeRatio: PropTypes.number.isRequired,
};

// this icon's ratio need to be reduced to 42% to match the filling area of the image.
export const BookIcon = ({ completeRatio, ...props }) => (
  <AnimatedFill
    width={119}
    height={150}
    progressType={PROGRESS_TYPE.BOOK}
    completeRatio={completeRatio * 0.42}
    {...props}
    iconAltText="Companion Reader"
  />
);

BookIcon.propTypes = {
  completeRatio: PropTypes.number.isRequired,
};

export const PortalIcon = (props) => (
  <AnimatedFill
    width={132}
    height={132}
    progressType={PROGRESS_TYPE.PORTAL}
    {...props}
    iconAltText="Trans-locational portal"
  />
);

export default AnimatedFill;
